
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Image } from "@/lib/types";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Film, ImageIcon } from "lucide-react";
import { toast } from "sonner";

interface AddImagesToAlbumProps {
  albumId: string;
  onSuccess: () => void;
}

const AddImagesToAlbum = ({ albumId, onSuccess }: AddImagesToAlbumProps) => {
  const [images, setImages] = useState<Image[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [existingImageIds, setExistingImageIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { session } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!session?.user) return;
      setLoading(true);

      try {
        // Get all images for the current user
        const { data: imagesData, error: imagesError } = await supabase
          .from('images')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false });

        if (imagesError) {
          console.error('Error fetching images:', imagesError);
          toast.error('Failed to load images');
          setImages([]);
          setLoading(false);
          return;
        }

        // Get existing images in the album
        const { data: albumImagesData, error: albumImagesError } = await supabase
          .from('album_images')
          .select('image_id')
          .eq('album_id', albumId);

        if (albumImagesError) {
          console.error('Error fetching album images:', albumImagesError);
        }

        const existingIds = albumImagesData?.map(item => item.image_id) || [];
        setExistingImageIds(existingIds);

        // Format images data
        if (imagesData && imagesData.length > 0) {
          const formattedImages = await Promise.all(imagesData.map(async (img) => {
            const { data: { publicUrl } } = supabase.storage
              .from('images')
              .getPublicUrl(img.storage_path);

            // Try to determine contentType from storage path
            let contentType: string | undefined = undefined;
            const extension = img.storage_path.split('.').pop()?.toLowerCase();
            
            if (extension) {
              if (['mp4', 'webm', 'mov'].includes(extension)) {
                contentType = `video/${extension === 'mov' ? 'quicktime' : extension}`;
              } else if (extension === 'gif') {
                contentType = 'image/gif';
              } else if (['jpg', 'jpeg', 'png', 'webp'].includes(extension)) {
                contentType = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
              }
            }

            return {
              id: img.id,
              name: img.name,
              description: img.description || undefined,
              url: publicUrl,
              slug: img.slug,
              createdAt: new Date(img.created_at).getTime(),
              views: img.views,
              contentType
            };
          }));

          setImages(formattedImages);
        } else {
          setImages([]);
        }
      } catch (err) {
        console.error('Error processing images:', err);
        toast.error('Error loading images');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [session, albumId]);

  const toggleSelectImage = (imageId: string) => {
    if (selectedImages.includes(imageId)) {
      setSelectedImages(selectedImages.filter(id => id !== imageId));
    } else {
      setSelectedImages([...selectedImages, imageId]);
    }
  };

  const handleSubmit = async () => {
    if (selectedImages.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    setSubmitting(true);
    try {
      const albumImagesData = selectedImages.map(imageId => ({
        album_id: albumId,
        image_id: imageId
      }));

      const { error } = await supabase
        .from('album_images')
        .insert(albumImagesData);

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast.warning('Some images are already in this album');
        } else {
          throw error;
        }
      }

      toast.success(`Added ${selectedImages.length} images to album`);
      onSuccess();
    } catch (error) {
      console.error('Error adding images to album:', error);
      toast.error('Failed to add images to album');
    } finally {
      setSubmitting(false);
    }
  };

  // Determine if image is a video based on contentType
  const getMediaType = (image: Image): 'video' | 'gif' | 'image' => {
    if (image.contentType) {
      if (image.contentType.startsWith('video/')) return 'video';
      if (image.contentType === 'image/gif') return 'gif';
    }
    
    // Fallback to URL extension checking
    const url = image.url.toLowerCase();
    if (url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov')) return 'video';
    if (url.endsWith('.gif')) return 'gif';
    
    return 'image';
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="aspect-video bg-black/30 rounded overflow-hidden">
            <Skeleton className="h-full w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="text-center py-8">
        <ImageIcon className="mx-auto h-12 w-12 opacity-50 mb-3" />
        <p className="text-white/70">You don't have any images to add</p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-[400px] overflow-y-auto p-1">
        {images.map(image => {
          const isInAlbum = existingImageIds.includes(image.id);
          const isSelected = selectedImages.includes(image.id);
          const mediaType = getMediaType(image);
          
          return (
            <div 
              key={image.id} 
              className={`relative aspect-video bg-black/20 rounded overflow-hidden border-2 ${
                isInAlbum ? 'border-yellow-500/50 opacity-50' : 
                isSelected ? 'border-primary' : 'border-transparent'
              } transition-all cursor-pointer`}
              onClick={() => {
                if (!isInAlbum) toggleSelectImage(image.id);
              }}
            >
              {mediaType === 'video' ? (
                <video 
                  src={image.url} 
                  className="w-full h-full object-cover" 
                  muted 
                  playsInline
                />
              ) : (
                <img 
                  src={image.url} 
                  alt={image.name} 
                  className="w-full h-full object-cover" 
                />
              )}
              
              {/* Media type indicator */}
              {mediaType === 'video' && (
                <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
                  <Film size={12} />
                  <span>Video</span>
                </div>
              )}
              
              {/* Selection checkbox */}
              <div className="absolute top-2 left-2">
                <Checkbox 
                  checked={isSelected} 
                  className={`h-5 w-5 ${isInAlbum ? 'opacity-50' : ''}`}
                  disabled={isInAlbum}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isInAlbum) toggleSelectImage(image.id);
                  }}
                />
              </div>
              
              {/* Image name */}
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 p-2 truncate">
                <p className="text-sm truncate">{image.name}</p>
              </div>
              
              {/* Already in album indicator */}
              {isInAlbum && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <div className="bg-yellow-500/80 text-black px-3 py-1 rounded-md text-sm font-medium">
                    Already in album
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      <div className="flex justify-between mt-6">
        <p className="text-sm text-white/70">
          {selectedImages.length} image{selectedImages.length !== 1 ? 's' : ''} selected
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => onSuccess()}>
            Cancel
          </Button>
          <Button 
            disabled={selectedImages.length === 0 || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Adding...' : 'Add to Album'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddImagesToAlbum;
